import React, { useContext, useEffect, useRef } from 'react';
import ServerDBContext from '../../components/ServerDBContext/ServerDBContext';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutOfAllGroups, ResetPosts, SelectLoadNewGroup, UpdateActiveGroupId, UpdateFellowshipLoaded, UpdateGroup, UpdateGroups, UpdateLoadNewGroup } from '../../store/FellowshipSlice';
import { Login, Logout, SelectCurrentUser, SelectLoginStatus, SelectNetworkConnected, UpdateNetworkConnected } from '../../store/UserSlice';
import IUser from '../../interfaces/IUser';
import IFellowshipGroup from '../../interfaces/IFellowshipGroup';
import UnregisterGroup from '../../data/UnregisteredGroup';
import IFellowshipGroups from '../../interfaces/IFellowshipGroups';
import FellowshipSettingsService from '../../services/FellowshipSettingsService';
import FellowshipPostService from '../../services/FellowshipPostService';
import IFellowshipSettings from '../../interfaces/IFellowshipSettings';
import NetworkStatusContext from '../NetworkStatusContext/NetworkStatusContext';
import ReadingPlanRecordService from '../../services/ReadingPlanRecordService';
import { createDefaultReadingPlanRecord } from '../../data/ReadingPlans';
import { SelectActiveReadingPlanRecord, UpdateActivePlanByIdAsync } from '../../store/BibleStudySlice';
import IReadingPlanRecord from '../../interfaces/IReadingPlanRecord';
import IApiResponse from '../../interfaces/IApiResponse';

const FellowshipConnect: React.FC = () => {

    const dispatch = useDispatch();
    const dbService = useContext(ServerDBContext);
    const NetworkService = useContext(NetworkStatusContext);
    const networkConnected = useSelector(SelectNetworkConnected);
    const loadNewGroup = useSelector(SelectLoadNewGroup);
    const LoggedIn = useSelector(SelectLoginStatus);
    const currentUser = useSelector(SelectCurrentUser);
    const activeReadingPlanRecord = useSelector(SelectActiveReadingPlanRecord);
    const disconnectedUser = useRef<IUser | undefined>(undefined);
    const callbackIndex = useRef<number | undefined>(undefined);

    useEffect(() => {
        dbService?.authenticated(onLogin, onLogout);

        callbackIndex.current = NetworkService?.setCallback((connected: boolean) => {
            if(connected){
                dispatch(UpdateNetworkConnected(true));
            } else if (!connected) {
                dispatch(UpdateNetworkConnected(false));
            }
        }, true);
        
        console.log('networkfuncIndex ', callbackIndex.current)
        return () => {
            if(dbService?.postSubscription) {
                dbService?.postSubscription();
            }
            if(callbackIndex.current !== undefined) {
                NetworkService?.clearCallback(callbackIndex.current);
            }
        }
    }, []);

    useEffect(() => {
        if(networkConnected && disconnectedUser.current) {
            // If Firebase is logged in and there is no group loaded run the onLogin when connected.
            // NOTE: onLogin has to run once and fail to get the group for this to run.
            onLogin(disconnectedUser.current);
        } 
    },[networkConnected]);

    useEffect(() => {
        if(LoggedIn && currentUser && networkConnected && loadNewGroup) {
            // loadNewGroup is set when a group is created
            onLogin(currentUser);
            dispatch(UpdateLoadNewGroup(false));
        }
    },[loadNewGroup]);

    const onLogin = (user: IUser) => {
        FellowshipSettingsService.getFellowshipSettings().then(resultSettings => {
            console.log('LOGGED IN');
            // reset any post subscriptions when logging into a group
            if(dbService) {
                dbService.postSubscription = undefined;
            }

            const defaultGroupId = resultSettings.Success ? resultSettings.Data?.defaultGroupId : undefined;
            const fellowshipNightMode =  resultSettings.Success && resultSettings.Data?.nightMode ? resultSettings.Data?.nightMode : false;

            console.log('fellowship-settings', resultSettings?.Data);

            dbService?.getUserGroups(user.id, defaultGroupId).then(async(result) => {
                if(result.Success){
                    const groups: IFellowshipGroup[] = result.Data!;
                    const userGroups: IFellowshipGroups = dbService?.getUserGroupsInfo(groups);
                    const activeGroup: IFellowshipGroup = defaultGroupId && userGroups.hasOwnProperty(defaultGroupId) ? userGroups[defaultGroupId].group : groups[0];
                    console.log('Active Group', activeGroup);
                    await Promise.all(groups.map(group =>
                        checkLastDates(group.id, userGroups)
                    ));
                    const settings: IFellowshipSettings = {
                        id: 1,
                        defaultGroupId: activeGroup.id,
                        nightMode: fellowshipNightMode,
                        userFellowshipGroups: userGroups,
                    }
                    FellowshipSettingsService.updateFellowshipSettings(settings);

                    const planRecords = await ReadingPlanRecordService.getReadingPlanRecords().then(result => result.Success ? result.Data : []);
                    const groupPlansResponse = await Promise.all(groups.map(group =>
                        syncLocalWithGroupPlanCode(group, planRecords)
                    ));
                    const groupPlans = groupPlansResponse.map(response => response?.Data).filter(plan => plan);
                    if(planRecords && groupPlans.length < planRecords.filter(plan => plan.groupId).length) {
                        // If there are group reading plans and there are more group reading plans than the user has groups, check to fix for disconnected plans
                        await restoreDisconnectedPlans(resultSettings.Data!, result.Data!, user.id, planRecords);
                    }
                    dispatch(UpdateGroups(userGroups));
                    dispatch(UpdateActiveGroupId(activeGroup.id))
                    dispatch(Login(user));
                    disconnectedUser.current = undefined;

                    dispatch(UpdateFellowshipLoaded(true));
                    const groupIds = userGroups ? Array.from(Object.keys(userGroups)) :  [];
                    UpdateActivePlanByIdAsync(dispatch, groupIds);
                    // If there is no active plan then make the active group's plan the active plan
                    // currently activeReadingPlanRecord has not been updated yet
                    // and I think the code is obsolete, I am leaving it for reference right now
                    // if(!activeReadingPlanRecord && groupPlans.length) {
                    //     const groupIds = userGroups ? Array.from(Object.keys(userGroups)) :  [];
                    //     let activeGroupPlan = groupPlans.find(plan => plan?.groupId === activeGroup.id);
                    //     activeGroupPlan = activeGroupPlan ? activeGroupPlan : groupPlans[0];
                    //     UpdateActivePlanByIdAsync(dispatch, groupIds, activeGroupPlan?.id);
                    // }
                    
                } else if (!result.Success && result.Message.ServerMessage === 'success-no-group') {
                    console.log(result.Message.UserMessage);
                    await restoreDisconnectedPlans(resultSettings.Data!, result.Data!, user.id);
                    dispatch(Login(user));
                    dispatch(UpdateGroups(null));
                    dispatch(ResetPosts());
                    dispatch(UpdateGroup(UnregisterGroup));
                    dispatch(UpdateFellowshipLoaded(true))
                    UpdateActivePlanByIdAsync(dispatch, []);
                }
                 else {
                    console.log("GroupLoadFailed");
                    dispatch(Login(user));
                    disconnectedUser.current = user;
                    dispatch(UpdateFellowshipLoaded(true))
                    UpdateActivePlanByIdAsync(dispatch, []);
                }
            }); 
        });
    }

    const onLogout = () => {
        console.log('Auth State Changed LOGGED OUT');
        if(dbService?.postSubscription) {
            dbService?.postSubscription();
        }
        dispatch(ResetPosts());
        dispatch(Logout());
        dispatch(LogoutOfAllGroups());
        UpdateActivePlanByIdAsync(dispatch, []);
    }

    const checkLastDates = async(groupId: string, groups: IFellowshipGroups) => {
        // this indicates that posts were retrieved in the past but never viewed.
        const localDbModifiedDate = await FellowshipPostService.getLastModifiedDate(groupId);
        // if no date found result is now date
        const lastModifiedDate = localDbModifiedDate.Success ? localDbModifiedDate.Data! : localDbModifiedDate.Data!;

        // get from local DB in FellowshipSettings.hasUnreadPosts if is true...if it is true set hasNewPosts to true
        const settingsResult = await FellowshipSettingsService.getFellowshipSettings();
        if (settingsResult.Success && 
            settingsResult.Data?.userFellowshipGroups?.hasOwnProperty(groupId)) {

                if(settingsResult!.Data.userFellowshipGroups[groupId].hasNewPosts) {
                    // Mutate Input as needed
                    groups[groupId].lastSortDate = settingsResult!.Data.userFellowshipGroups[groupId].lastSortDate;
                    groups[groupId].lastModifiedDate = lastModifiedDate.getTime();
                    groups[groupId].hasNewPosts = true;
                    return false;
                }
        }
        // this indicates that posts were retrieved in the past but never viewed.
        const localDbLastSortDate = await FellowshipPostService.getLastSortDate(groupId);
        // if no date found result is now date
        const lastSortDate = localDbLastSortDate.Success ? localDbLastSortDate.Data! : localDbLastSortDate.Data!;

        // Mutate Input as needed
        groups[groupId].lastSortDate = lastSortDate.getTime();
        groups[groupId].lastModifiedDate = lastModifiedDate.getTime();

        // Check Cloud DB for newer posts
        const startDate = new Date(lastModifiedDate);
        startDate.setMilliseconds(lastModifiedDate.getMilliseconds() + 100);
        const cloudLastModifiedPostResult = await dbService?.getLastModifiedPosts(startDate, 1);
        console.log('Connect-Last-Cloud-Mod-Date', localDbModifiedDate, cloudLastModifiedPostResult);
        if(cloudLastModifiedPostResult?.Success) {
            // Mutate Input as needed
            groups[groupId].hasNewPosts = !!cloudLastModifiedPostResult.Data?.length;
            return true;
        } else {
            // Mutate Input as needed
            groups[groupId].hasNewPosts = false;
            return false;
        }
    }

    const syncLocalWithGroupPlanCode = async (group: IFellowshipGroup, planRecords?: IReadingPlanRecord[]) => {
        const codeData = group?.syncCode ? ReadingPlanRecordService.interpretSyncCode!(group.syncCode) : undefined;
        let updateGroupPlan = undefined;
        let apiResponse:Promise<IApiResponse<IReadingPlanRecord>> | undefined = undefined;
        const groupPlanRecord = planRecords?.find(record => record.groupId === group?.id);
    
        if (codeData)
        {
            const {readingPlanCode, sessionsPerDay, startDate} = codeData;
            
            if(groupPlanRecord && groupPlanRecord.readingPlanCode === readingPlanCode) {
                // Only update the existing plan if something has changed
                if(groupPlanRecord.startDate.getTime() !== startDate.getTime() || 
                    groupPlanRecord.sessionsPerDay !== sessionsPerDay || 
                    groupPlanRecord.name !== group.name) {
                    const name = group.name;
                    updateGroupPlan = {...groupPlanRecord, sessionsPerDay, startDate, name};  
                    apiResponse = ReadingPlanRecordService.updateReadingPlanRecord(updateGroupPlan);
                } else {
                    apiResponse = Promise.resolve({Success: true, Data: groupPlanRecord, Message: {UserMessage: 'No Changes', ServerMessage: 'success'}});
                }
            } else if(groupPlanRecord) {
                // The existing plan's type has changed therefore convert the old plan to a personal plan
                const name = `${groupPlanRecord.name} (OLD ${new Date(Date.now()).toLocaleDateString()})`;
                const oldGroupPlan = {...groupPlanRecord, name, groupId: ''};  
                ReadingPlanRecordService.updateReadingPlanRecord(oldGroupPlan);
                // Create a new local group plan
                updateGroupPlan = createDefaultReadingPlanRecord(readingPlanCode);
                updateGroupPlan = {...updateGroupPlan, sessionsPerDay, startDate};
                updateGroupPlan.groupId = group?.id;
                updateGroupPlan.name = group.name;
                apiResponse = ReadingPlanRecordService.createReadingPlanRecord(updateGroupPlan);
            } else {
                // No local group plan found, therefore create one
                updateGroupPlan = createDefaultReadingPlanRecord(readingPlanCode);
                updateGroupPlan = {...updateGroupPlan, sessionsPerDay, startDate};
                updateGroupPlan.groupId = group?.id;
                updateGroupPlan.name = group.name;
                apiResponse = ReadingPlanRecordService.createReadingPlanRecord(updateGroupPlan);
            }
        } else if (groupPlanRecord) {
            // If the group has removed its reading plan and no plan exists anymore
            // convert old group plan into a personal plan with updated name.
            const name = `${group.name} (OLD)`;
            updateGroupPlan = {...groupPlanRecord, name, groupId: ''};  
            apiResponse = ReadingPlanRecordService.updateReadingPlanRecord(updateGroupPlan);
        }
    
          return apiResponse;
    }

    const restoreDisconnectedPlans = async (fellowshipSettings: IFellowshipSettings, remoteGroups: IFellowshipGroup[], userId: string, planRecords?: IReadingPlanRecord[]) => {
        const userLocalGroupArray = fellowshipSettings?.userFellowshipGroups ? Object.values(fellowshipSettings.userFellowshipGroups): [];
        if(userLocalGroupArray.length) {
            const userLocalGroup = userLocalGroupArray.filter(data => data.group.memberIds.find(id => id === userId)).map(groupData => groupData.group);
            if(userLocalGroup.length) {
                const disconnectLocalGroups = remoteGroups.length ? userLocalGroup.filter(localGroup => !remoteGroups.find(remoteGroup => remoteGroup.id === localGroup.id)) : userLocalGroup;
                if(disconnectLocalGroups.length) {
                    let readingPlanRecords: IReadingPlanRecord[] = [];
                    if(planRecords) {
                        readingPlanRecords = planRecords ? [...planRecords] : [];
                    } else {
                        readingPlanRecords = await ReadingPlanRecordService.getReadingPlanRecords().then(result => result.Success ? result.Data : []) as IReadingPlanRecord[];
                    }
                    if(readingPlanRecords.length) {
                        const disconnectPlans = readingPlanRecords.filter(plan => disconnectLocalGroups.find(disconnectedGroup => disconnectedGroup.id === plan.groupId));
                        if(disconnectPlans.length) {
                            await disconnectPlans.forEach(async (disconnectPlan) => {
                                const updatedPlan  = {...disconnectPlan};
                                updatedPlan.groupId = '';
                                updatedPlan.name = `OLD GROUP: ${updatedPlan.name}`;
                                const updateResult = await ReadingPlanRecordService.updateReadingPlanRecord(updatedPlan);
                                console.log('Disconnected Plan Update', updateResult);
                            })
                        }
                    }
                }
            }
        }
    }

    return <div></div>
};

export default FellowshipConnect;